import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Dropdown, Button, Grid, Input, Segment, Icon, Label } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { VARIATION_STATUSES } from './variationStatusConfig';
import {
  fetchFilterOptions,
  fetchBooksByLitGroup
} from '../../slices/opportunityFiltersSlice';


/**
 * OpportunityFilter component provides filtering options for opportunities
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onFilterChange - Function to call when filters change
 * @param {Object} props.initialFilters - Initial filter values
 * @param {Array} props.books - Array of books for the book filter dropdown
 * @param {Array} props.characters - Array of characters for the character filter dropdown
 * @param {boolean} props.compact - Whether to display the filter in compact mode
 */
const OpportunityFilter = ({
  onFilterChange,
  initialFilters = {},
  books = [],
  characters = [],
  compact = false,
  // When set, Submit stays disabled until a Literary Group is chosen. The page that uses it
  // loads per group and shows nothing at all until one is picked, so an enabled Submit that
  // did nothing would be the crueller design.
  requireLitGroup = false,

  /**
   * 'idle' | 'loading' | 'succeeded' | 'failed' — the REAL fetch status from the store, not a
   * local guess.
   *
   * ⭐ THE INDICATOR BELONGS HERE, beside the button that started the work. These filters take long
   * enough that a user reasonably wonders whether the click registered, and a spinner somewhere
   * else on the page does not answer that question — it answers a different one.
   *
   * ⚠️ Optional on purpose: this component has two callers and one of them may not track status.
   * Undefined means "show nothing", never "show idle".
   */
  fetchStatus
}) => {
  // Opportunity-level status options (Opportunity / Character status).
  // NOTE: underlying filter key stays `status` for URL/backend compatibility.
  const statusOptions = [
    { key: 'all', text: 'All Opportunity Statuses', value: 'all' },
    { key: 'open', text: 'Open', value: 'Open' },
    { key: 'assigned', text: 'Assigned', value: 'Assigned' },
    { key: 'completed', text: 'Completed', value: 'Completed' },
    { key: 'cancelled', text: 'Cancelled', value: 'Cancelled' }
  ];

  // Variation-level status options (per-recording sample status).
  // Applied client-side; the dropdown also acts as a mini-legend since
  // each option renders with its colored dot and icon.
  const variationStatusOptions = [
    { key: 'all', text: 'All Variation Statuses', value: 'all' },
    ...VARIATION_STATUSES.map((s) => ({
      key: s.key,
      value: s.key,
      text: s.label,
      content: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <Label empty circular color={s.color} size="mini" />
          <Icon name={s.icon} style={{ margin: 0 }} />
          {s.label}
        </span>
      )
    }))
  ];


  // Priority options
  const priorityOptions = [
    { key: 'all', text: 'All Priorities', value: 'all' },
    { key: 'high', text: 'High', value: 'High' },
    { key: 'medium', text: 'Medium', value: 'Medium' },
    { key: 'low', text: 'Low', value: 'Low' }
  ];

  // ── LitGroup, and the Books inside it ─────────────────────────────────────────────────────
  //
  // ⭐ THE LIST IS UNUSABLE WITHOUT THIS. Characters are LitGroup-shared, so an unscoped list
  // interleaves characters from every series — and since different series legitimately contain
  // characters of the same name, choosing the wrong row is easy and silent. The Book filter alone
  // could not fix that: `books` arrives as a PROP and ManageOpportunities passes none, so the
  // dropdown has only ever offered "All Books".
  //
  // Loading its own options rather than requiring a parent to feed it — no caller does, which is
  // why the control was inert. Same endpoints and same cascade as the marketplace filter.
  const dispatch = useDispatch();
  const filterOptions = useSelector((state) => state.user?.opportunityFilters?.filterOptions);
  const litGroups = filterOptions?.litGroups || [];
  // Books for the CHOSEN group; falls back to the prop so existing callers that do supply books
  // keep working exactly as before.
  const groupBooks = filterOptions?.books;

  useEffect(() => {
    if (!litGroups.length) dispatch(fetchFilterOptions());
  }, [dispatch, litGroups.length]);

  const litGroupOptions = [
    { key: 'all', text: 'All Literary Groups', value: 'all' },
    ...litGroups.map((lg) => ({ key: lg.uuid, text: lg.name, value: lg.uuid }))
  ];

  // Format book options
  const bookSource = (groupBooks && groupBooks.length) ? groupBooks : books;
  const bookOptions = [
    { key: 'all', text: 'All Books', value: 'all' },
    ...bookSource.map(book => ({
      key: book.uuid,
      text: book.name || book.title,
      value: book.uuid
    }))
  ];

  // Format character options
  const characterOptions = [
    { key: 'all', text: 'All Characters', value: 'all' },
    ...characters.map(character => ({
      key: character.uuid,
      text: character.name,
      value: character.uuid
    }))
  ];

  // State for filters
  const [filters, setFilters] = useState({
    status: 'all',
    variationStatus: 'all',
    priority: 'all',
    litGroupId: 'all',
    bookId: 'all',
    characterId: 'all',
    searchTerm: '',
    ...initialFilters
  });

  // Choosing a group refills the Book list and clears any book from the previous group — leaving
  // it selected would filter to a book that is not in the group now showing, which returns
  // nothing and looks like a broken filter rather than a stale one.
  useEffect(() => {
    if (filters.litGroupId && filters.litGroupId !== 'all') {
      dispatch(fetchBooksByLitGroup(filters.litGroupId));
    }
  }, [dispatch, filters.litGroupId]);


  // ---------------------------------------------------------------------------
  // NOTHING FETCHES UNTIL SUBMIT.
  //
  // Changing a dropdown used to fire `onFilterChange` from an effect, so every touch of the
  // form was a round trip. Composing a selection takes three or four touches -- group, then
  // book, then character -- and the intermediate ones are not questions anyone wanted asked:
  // picking a Literary Group before its Book fetches the whole group's cast, which is the
  // slowest query on the page and never the one being composed.
  //
  // Worse, it made the form lie in flight. Choosing a group clears the book beneath it (a book
  // from the previous group would filter to something not in the group now showing), so a
  // group change fired a request for a state the operator was mid-way through abandoning.
  //
  // The button is the only thing that fetches now. `appliedFilters` remembers what was last
  // submitted so the form can say whether what you are looking at matches what you asked for.
  // ---------------------------------------------------------------------------

  /** Strip the values that mean "no opinion" -- the API reads absence, not 'all'. */
  const toApiFilters = (f) => Object.fromEntries(
    Object.entries(f).filter(([, value]) => value !== 'all' && value !== '')
  );

  // What was last submitted. Starts at the initial state so an untouched form is not "dirty".
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...filters }));

  // True once this panel has submitted at least once — see applyFilters and renderFetchState.
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const isDirty = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(appliedFilters),
    [filters, appliedFilters]
  );

  const scopeMissing = requireLitGroup && (!filters.litGroupId || filters.litGroupId === 'all');
  const canSubmit = isDirty && !scopeMissing;

  // One line, and it says which of the two states you are in — nothing chosen yet, or chosen
  // and not yet asked for. Silence on either would leave the button looking broken.
  const submitHint = scopeMissing
    ? 'Choose a Literary Group to load opportunities'
    : (isDirty ? 'Filters changed — press Submit to apply' : '');

  const applyFilters = useCallback((next) => {
    const target = next || filters;
    setAppliedFilters({ ...target });
    // ⚠️ Marks that THIS panel started a fetch. Without it, a page-level load on mount would light
    // up a "filter complete" tick for work the user never asked for.
    setHasSubmitted(true);
    onFilterChange(toApiFilters(target));
  }, [filters, onFilterChange]);

  const busy = fetchStatus === 'loading';

  /**
   * Progress beside the button that started the work.
   *
   * ⭐ These filters take long enough that a user reasonably wonders whether the click registered.
   * A spinner elsewhere on the page answers a different question; this one answers theirs.
   *
   * ⚠️ `aria-live="polite"` so the transition is announced rather than only seen — the same
   * treatment the existing submit hint already gets in this component.
   */
  const renderFetchState = () => {
    if (!hasSubmitted || !fetchStatus) return null;

    // Inline, matching the submit-hint span a few lines below — this component imports no
    // stylesheet of its own and adding one for three words would be the odd choice.
    const base = { marginRight: '0.75em', fontSize: '0.9em', whiteSpace: 'nowrap' };

    if (busy) {
      return (
        <span style={{ ...base, opacity: 0.75 }} aria-live="polite">
          <Icon name="circle notch" loading /> Filtering…
        </span>
      );
    }
    if (fetchStatus === 'failed') {
      return (
        <span style={{ ...base, color: '#b32424' }} aria-live="polite">
          <Icon name="warning sign" /> Filter failed
        </span>
      );
    }
    if (fetchStatus === 'succeeded') {
      return (
        <span style={{ ...base, color: '#065f46' }} aria-live="polite">
          <Icon name="check" /> Updated
        </span>
      );
    }
    return null;
  };

  // Enter in the search box submits, the way any search box should.
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!scopeMissing) applyFilters();
    }
  };

  // Handle filter changes
  const handleFilterChange = (e, { name, value }) => {
    setFilters(prev => (
      // Changing the group invalidates the book chosen inside the previous one.
      name === 'litGroupId'
        ? { ...prev, litGroupId: value, bookId: 'all' }
        : { ...prev, [name]: value }
    ));
  };

  // Handle search input change
  const handleSearchChange = (e, { value }) => {
    setFilters(prev => ({ ...prev, searchTerm: value }));
  };

  // Clear all filters. This one DOES fetch, and that is not a contradiction: a button press is
  // an explicit action. A Clear that emptied the form and left the old rows on screen would
  // read as broken, and would need a second click to mean anything.
  const handleClearFilters = () => {
    const cleared = {
      status: 'all',
      variationStatus: 'all',
      priority: 'all',
      litGroupId: 'all',
      bookId: 'all',
      characterId: 'all',
      searchTerm: ''
    };
    setFilters(cleared);
    applyFilters(cleared);
  };


  // Render compact filter
  if (compact) {
    return (
      <Segment>
        <Grid stackable>
          {/* ⭐ ROWS STACK IN DOCUMENT ORDER, SO THEIR z-index MUST DESCEND.
            *
            * A Semantic dropdown menu opens DOWNWARD and is absolutely positioned inside its own
            * row. Without an explicit stacking order the later row paints over the earlier one,
            * so opening Opportunity Status or Variation Status drops its menu BEHIND the Literary
            * Group and Book controls below them — the menu is there, and unclickable.
            *
            * Each row needs position: relative for z-index to apply at all (static elements
            * ignore it). Any row added below this pair takes a LOWER number than the pair. */}
          <Grid.Row style={{ position: 'relative', zIndex: 30 }}>
            <Grid.Column width={8}>
              <Input
                fluid
                icon="search"
                placeholder="Search opportunities..."
                name="searchTerm"
                value={filters.searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Dropdown
                fluid
                selection
                name="status"
                options={statusOptions}
                value={filters.status}
                onChange={handleFilterChange}
                placeholder="Opportunity Status"
                aria-label="Filter by opportunity status"
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Dropdown
                fluid
                selection
                name="variationStatus"
                options={variationStatusOptions}
                value={filters.variationStatus}
                onChange={handleFilterChange}
                placeholder="Variation Status"
                aria-label="Filter by variation sample status"
              />
            </Grid.Column>
          </Grid.Row>

          {/* ⭐ SCOPE. On its own row because it is a different KIND of filter: the row above
            * narrows which opportunities you look at, this one decides which CAST you are
            * looking at at all.
            *
            * Characters are LitGroup-shared, so an unscoped list interleaves every series —
            * measured, 1,000 rows across 11 groups — and different series legitimately hold
            * characters of the same name, so choosing the wrong row is easy and silent. */}
          <Grid.Row style={{ position: 'relative', zIndex: 20 }}>
            <Grid.Column width={8}>
              <Dropdown
                fluid
                selection
                search
                name="litGroupId"
                options={litGroupOptions}
                value={filters.litGroupId}
                onChange={handleFilterChange}
                placeholder="Literary Group"
                aria-label="Filter by Literary Group"
              />
            </Grid.Column>
            <Grid.Column width={8}>
              {/* Book REFINES the group rather than replacing it — the server keeps both clauses.
                * Left at All Books it shows the whole shared cast, which is the common case:
                * characters belong to the series, not to one volume of it. */}
              <Dropdown
                fluid
                selection
                name="bookId"
                options={bookOptions}
                value={filters.bookId}
                onChange={handleFilterChange}
                disabled={filters.litGroupId === 'all' && bookOptions.length <= 1}
                placeholder={filters.litGroupId === 'all' ? 'Book — choose a group first' : 'All Books'}
                aria-label="Filter by book"
              />
            </Grid.Column>
          </Grid.Row>

          {/* Submit. Below the dropdown rows, so it takes a LOWER z-index than the pair above
            * (see the stacking note on the first row) -- an open menu must paint over it. */}
          <Grid.Row style={{ position: 'relative', zIndex: 10 }}>
            <Grid.Column width={16} textAlign="right">
              {submitHint && (
                <span
                  style={{ marginRight: '0.75em', opacity: 0.7, fontStyle: 'italic' }}
                  aria-live="polite"
                >
                  {submitHint}
                </span>
              )}
              {renderFetchState()}
              <Button type="button" size="small" onClick={handleClearFilters} disabled={busy}>
                Clear
              </Button>
              <Button
                type="button"
                size="small"
                primary
                loading={busy}
                disabled={!canSubmit || busy}
                onClick={() => applyFilters()}
              >
                <Icon name="filter" /> Submit
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
    );
  }


  // Render full filter
  return (
    <Segment>
      <Form>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={4}>
              <label>Search</label>
              <Input
                fluid
                icon="search"
                placeholder="Search opportunities..."
                name="searchTerm"
                value={filters.searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <label>Opportunity Status</label>
              <Dropdown
                fluid
                selection
                name="status"
                options={statusOptions}
                value={filters.status}
                onChange={handleFilterChange}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <label>Variation Status</label>
              <Dropdown
                fluid
                selection
                name="variationStatus"
                options={variationStatusOptions}
                value={filters.variationStatus}
                onChange={handleFilterChange}
              />
            </Grid.Column>
            <Grid.Column width={3}>

              <label>Priority</label>
              <Dropdown
                fluid
                selection
                name="priority"
                options={priorityOptions}
                value={filters.priority}
                onChange={handleFilterChange}
              />

            </Grid.Column>

            <Grid.Column width={3}>

              <label>Literary Group</label>
              <Dropdown
                fluid
                selection
                search
                name="litGroupId"
                options={litGroupOptions}
                value={filters.litGroupId}
                onChange={handleFilterChange}
              />

            </Grid.Column>

            <Grid.Column width={3}>

              {/* Book REFINES the group rather than replacing it — the server keeps both clauses,
                * so LitGroup + Book narrows to that book inside that series. Left at All Books it
                * shows the whole shared cast, which is the common case: characters belong to the
                * series, not to one volume of it. */}
              <label>Book</label>
              <Dropdown
                fluid
                selection
                name="bookId"
                options={bookOptions}
                value={filters.bookId}
                onChange={handleFilterChange}
                disabled={filters.litGroupId === 'all' && bookOptions.length <= 1}
                placeholder={filters.litGroupId === 'all' ? 'Choose a group first' : 'All Books'}
              />

            </Grid.Column>
            <Grid.Column width={3}>

              <label>Character</label>
              <Dropdown
                fluid
                selection
                name="characterId"
                options={characterOptions}
                value={filters.characterId}
                onChange={handleFilterChange}
              />

            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16} textAlign="right">
              {submitHint && (
                <span
                  style={{ marginRight: '0.75em', opacity: 0.7, fontStyle: 'italic' }}
                  aria-live="polite"
                >
                  {submitHint}
                </span>
              )}
              {renderFetchState()}
              <Button type="button" onClick={handleClearFilters} disabled={busy}>
                Clear Filters
              </Button>
              <Button
                type="button"
                primary
                loading={busy}
                disabled={!canSubmit || busy}
                onClick={() => applyFilters()}
              >
                <Icon name="filter" /> Submit
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </Segment>
  );
};

OpportunityFilter.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape({
    status: PropTypes.string,
    priority: PropTypes.string,
    bookId: PropTypes.string,
    characterId: PropTypes.string,
    searchTerm: PropTypes.string
  }),
  books: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  characters: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  compact: PropTypes.bool,
  requireLitGroup: PropTypes.bool,
  // Not required: undefined means "this caller does not track status", and the indicator hides.
  fetchStatus: PropTypes.oneOf(['idle', 'loading', 'succeeded', 'failed'])
};

export default OpportunityFilter;
